import React, {useRef, useState} from 'react';
import ModalContainer from '../../../../uiToolkit/ModalContainer';
import SimplePlayerControlled from '../../../../uiToolkit/SimplePlayerControlled';
import {addFreeformChoiceText} from '../addFreeformChoiceText';
import './styles.css';
import FreeformImageOptions from '../components/FreeformImageOptions';
import {
  BRANCH_IMAGE_FREEFORM,
  BRANCH_SHAPE_FREEFORM,
  BRANCH_TEXT_FREEFORM,
  LessonContentBase,
  LessonContentFreeformImageChoice,
  LessonContentFreeformShapeChoice,
  LessonContentFreeformTextChoice,
} from '../../../../data/models/LessonContent/LessonContentBase';
import EditorSvgCanvas from '../../FreeformElements/SvgCanvas';
import EditorSvgPath from '../../FreeformElements/EditorSvgPath';
import EditorSvgImage from '../../FreeformElements/EditorSvgImage';
import EditorSvgText from '../../FreeformElements/EditorSvgText';
import {
  addPointToOverlay,
  removePointFromOverlay,
} from '../../FreeformElements/EditorSvgPath/utils';
import Button from '../../../../uiToolkit/Buttons/Button';
import {
  Point,
  SvgImage,
} from '../../../../data/models/LessonContent/ContentFreeform';
import FreeformTextOptions from '../components/FreeformTextOptions';
import FreeformShapeOptions from '../components/FreeformShapeOptions';

interface Props {
  onClose: () => void;
  videoSource?: string;
  imageSource?: string;
  videoEndTime?: number;
  currentNodeId: string;
  saveChanges: (options: LessonContentBase[]) => void;
  branchOptions: LessonContentBase[];
  isLoading: boolean;
  question: string;
}

const AddFreeformChoiceUI = React.memo(
  ({
    onClose,
    videoSource,
    videoEndTime,
    currentNodeId,
    saveChanges,
    branchOptions,
    imageSource,
    isLoading,
    question,
  }: Props) => {
    const [width, setWidth] = useState(0);
    const [activeNodeId, setActiveNodeId] = useState<string>();
    const [options, setOptions] = useState<LessonContentBase[]>([]);
    const containerRef = useRef<any>(null);

    React.useEffect(() => {
      function handleResize() {
        if (containerRef && containerRef.current) {
          setWidth(containerRef.current.offsetWidth);
        }
      }

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    React.useLayoutEffect(() => {
      if (containerRef && containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    }, []);

    React.useEffect(() => {
      if (currentNodeId) {
        setActiveNodeId(currentNodeId);
      }
    }, [currentNodeId]);

    React.useEffect(() => {
      if (!options.length) {
        setOptions(branchOptions);
      }
    }, [branchOptions]);

    const onSave = () => {
      saveChanges(options);
    };

    const onChangeActiveNodeField = (field: string, value: string) => {
      const activeNode = options.find(x => x.internalId === activeNodeId);
      if (activeNode) {
        const updatedNode: LessonContentBase = {
          ...activeNode,
          [field]: value,
        };
        const activeNodeIndex = options.findIndex(
          x => x.internalId === activeNodeId,
        );
        const updatedOptions = [...options];
        updatedOptions.splice(activeNodeIndex, 1, updatedNode);
        setOptions(updatedOptions);
      }
    };

    const getActiveNode = () => {
      return options.find(x => x.internalId === activeNodeId);
    };

    const updateImageSvg = (imageOptions: SvgImage) => {
      const activeNode = options.find(x => x.internalId === activeNodeId);
      if (activeNode && activeNode.ivNodeType === BRANCH_IMAGE_FREEFORM) {
        const updatedNode: LessonContentFreeformImageChoice = {
          ...(activeNode as LessonContentFreeformImageChoice),
          ...imageOptions,
        };
        const activeNodeIndex = options.findIndex(
          x => x.internalId === activeNodeId,
        );
        const updatedOptions = [...options];
        updatedOptions.splice(activeNodeIndex, 1, updatedNode);
        setOptions(updatedOptions);
      }
    };

    const updateTextSvg = (position: Point) => {
      const activeNode = options.find(x => x.internalId === activeNodeId);
      if (activeNode && activeNode.ivNodeType === BRANCH_TEXT_FREEFORM) {
        const updatedNode: LessonContentFreeformTextChoice = {
          ...(activeNode as LessonContentFreeformTextChoice),
          ...position,
        };
        const activeNodeIndex = options.findIndex(
          x => x.internalId === activeNodeId,
        );
        const updatedOptions = [...options];
        updatedOptions.splice(activeNodeIndex, 1, updatedNode);
        setOptions(updatedOptions);
      }
    };

    const updateShapeSvg = (points: Point[]) => {
      const activeNode = options.find(x => x.internalId === activeNodeId);
      if (activeNode && activeNode.ivNodeType === BRANCH_SHAPE_FREEFORM) {
        const updatedNode: LessonContentFreeformShapeChoice = {
          ...(activeNode as LessonContentFreeformShapeChoice),
          overlayCoordinates: [...points],
        };
        const activeNodeIndex = options.findIndex(
          x => x.internalId === activeNodeId,
        );
        const updatedOptions = [...options];
        updatedOptions.splice(activeNodeIndex, 1, updatedNode);
        setOptions(updatedOptions);
      }
    };

    const addShapePoint = () => {
      const activeNode = options.find(x => x.internalId === activeNodeId);
      if (activeNode && activeNode.ivNodeType === BRANCH_SHAPE_FREEFORM) {
        const newPoints = addPointToOverlay(
          (activeNode as LessonContentFreeformShapeChoice).overlayCoordinates,
        );
        const updatedShape = {
          ...activeNode,
          overlayCoordinates: newPoints,
        };
        const activeNodeIndex = options.findIndex(
          x => x.internalId === activeNodeId,
        );
        const updatedOptions = [...options];
        updatedOptions.splice(activeNodeIndex, 1, updatedShape);
        setOptions(updatedOptions);
      }
    };

    const removeShapePoint = () => {
      const activeNode = options.find(x => x.internalId === activeNodeId);
      if (activeNode && activeNode.ivNodeType === BRANCH_SHAPE_FREEFORM) {
        const newPoints = removePointFromOverlay(
          (activeNode as LessonContentFreeformShapeChoice).overlayCoordinates,
        );
        const updatedShape = {
          ...activeNode,
          overlayCoordinates: newPoints,
        };
        const activeNodeIndex = options.findIndex(
          x => x.internalId === activeNodeId,
        );
        const updatedOptions = [...options];
        updatedOptions.splice(activeNodeIndex, 1, updatedShape);
        setOptions(updatedOptions);
      }
    };

    const getOptionsRenders = (): React.ReactElement[] => {
      return options.map((node, index) => {
        if (node.ivNodeType === BRANCH_IMAGE_FREEFORM) {
          const imageNode = node as LessonContentFreeformImageChoice;
          return (
            <EditorSvgImage
              image={{...imageNode}}
              id={imageNode.internalId}
              key={imageNode.internalId}
              source={imageNode.contentUrl || ''}
              aspect={imageNode.aspect}
              onClick={() => setActiveNodeId(imageNode.internalId)}
              updateImageOptions={
                activeNodeId === imageNode.internalId
                  ? updateImageSvg
                  : undefined
              }
              width={width}
            />
          );
        }
        if (node.ivNodeType === BRANCH_TEXT_FREEFORM) {
          const textNode = node as LessonContentFreeformTextChoice;
          return (
            <EditorSvgText
              position={{...textNode}}
              text={textNode.text}
              color={textNode.color}
              key={textNode.internalId}
              onClick={() => setActiveNodeId(textNode.internalId)}
              updateTextOptions={
                activeNodeId === textNode.internalId ? updateTextSvg : undefined
              }
              selected={activeNodeId === textNode.internalId}
              width={width}
            />
          );
        }
        if (node.ivNodeType === BRANCH_SHAPE_FREEFORM) {
          const shapeNode = node as LessonContentFreeformShapeChoice;
          return (
            <EditorSvgPath
              shapePoints={shapeNode.overlayCoordinates}
              key={shapeNode.internalId}
              onClick={() => setActiveNodeId(shapeNode.internalId)}
              updateShapePoints={
                activeNodeId === shapeNode.internalId
                  ? updateShapeSvg
                  : undefined
              }
              width={width}
            />
          );
        }
        return <g key={index}></g>;
      });
    };

    return (
      <ModalContainer onClose={onClose}>
        <div className="AddFreeformOverlay">
          <div>
            <p className="SectionHeader">
              {addFreeformChoiceText.lastVideoFrame}
            </p>
            <div className="PlayerContainer" ref={containerRef}>
              {videoSource && (
                <SimplePlayerControlled
                  width={width}
                  source={videoSource}
                  startTime={videoEndTime}
                  endTime={videoEndTime}
                  hideControls
                />
              )}
              {imageSource && (
                <div
                  style={{width, height: width / 1.77}}
                  className="ImageSourceContainer">
                  <img src={imageSource} />
                  <span
                    style={{
                      backgroundImage: `url(${imageSource})`,
                    }}></span>
                </div>
              )}
              <div className="QuestionHeader">
                <p>{question}</p>
              </div>
              <EditorSvgCanvas width={width} showGuidelines>
                <g>{getOptionsRenders()}</g>
              </EditorSvgCanvas>
            </div>
            <div className="AddFreeformFooter">
              <Button
                uiType="hollow"
                onClick={onClose}
                title={addFreeformChoiceText.cancel}
              />
              <Button
                uiType="action"
                onClick={onSave}
                title={addFreeformChoiceText.save}
                isLoading={isLoading}
              />
            </div>
          </div>
          {getActiveNode()?.ivNodeType === BRANCH_IMAGE_FREEFORM && (
            <FreeformImageOptions
              imageNode={getActiveNode() as LessonContentFreeformImageChoice}
              onChangeImageField={onChangeActiveNodeField}
            />
          )}
          {getActiveNode()?.ivNodeType === BRANCH_TEXT_FREEFORM && (
            <FreeformTextOptions
              textNode={getActiveNode() as LessonContentFreeformTextChoice}
              onChangeTextField={onChangeActiveNodeField}
            />
          )}
          {getActiveNode()?.ivNodeType === BRANCH_SHAPE_FREEFORM && (
            <FreeformShapeOptions
              shapeNode={getActiveNode() as LessonContentFreeformShapeChoice}
              onChangeShapeField={onChangeActiveNodeField}
              onAddShapePoint={addShapePoint}
              onRemoveShapePoint={removeShapePoint}
            />
          )}
        </div>
      </ModalContainer>
    );
  },
);

export default AddFreeformChoiceUI;
